import { searchRecipeLink, supportedRecipeSites } from "@/lib/scrape-utilities";
import useIndexedDB from "@/lib/useIndexedDB";
import { GlobeAltIcon } from "@heroicons/react/24/outline";
import cuid from "cuid";
import { combineUnits, searchForUnit } from "@/lib/measurement";
import { ScrapedRecipeData } from "@/lib/types";
import useDebounce from "@/lib/useDebounce";
import Link from "next/link";
import {
  SearchIngredientsDocument,
  SearchIngredientsQuery,
  SearchIngredientsQueryVariables,
} from "@/queries/autogenerate/graphql";
import { RefObject, useContext, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { compareTwoStrings } from "string-similarity";
import styled from "styled-components";
import { useClient } from "urql";
import { CurrentUser } from "../Page";
import BouncingLoader from "../styles/BouncingLoader";
import FinalisePartialRecipe, { PartialRecipe } from "./FinalisePartialRecipe";
import { loadPartialRecipeIngredientsToStore } from "./util";
import { IngredientsContext } from "@/app/clientLayout";
import { searchInventory } from "@/lib/util";

const URLForm = styled.form`
  width: 400px;
  /* padding: 5px; */

  label {
    flex: 0;
    text-align: left;
    font-size: small;

    ul {
      list-style: disc;

      li {
        margin-left: 20px;
      }
    }
  }

  /* input {
    padding: 5px;
    min-height: 38px;
    width: 300px;
    border: 1px solid hsl(0, 0%, 80%);
    border-radius: 4px;
  } */
`;

interface Props {
  setURLFn?: (URL: string) => void;
  onCompleted?: () => void;
  user: CurrentUser;
}

const FetchRecipe: React.FC<Props> = ({ setURLFn, onCompleted, user }) => {
  const client = useClient();

  const { ingredients } = useContext(IngredientsContext);

  const [URL, setURL] = useIndexedDB(
    "fetchRecipeURL",
    "",
    !user.saveRecipeProgress
  );
  const [loading, setLoading] = useState(false);
  const [recipeData, setRecipeData] = useIndexedDB<PartialRecipe[] | null>(
    "fetchRecipeData3",
    null,
    !user.saveRecipeProgress
  );

  const textInput = useRef<HTMLInputElement>(null);

  // This is important to prevent refetching when a user comes back to finish a recipe
  // In that case, the details have already been loaded, so there is no need to refetch
  // We can't set the URL to be empty because then UploadRecipe won't know to hide other methods
  // And thus we arrive at this solution
  const [canUseURL, setCanUseURL] = useState(false);
  const debouncedURL = useDebounce(URL, 500);

  // Here's where the API call happens
  // We use useEffect since this is an asynchronous action
  useEffect(
    () => {
      // Set up an abort controller, to abort on exit
      const abortController = new AbortController();

      // Make sure we have a value (user has entered something in input)
      if (debouncedURL && canUseURL) {
        console.log("Let's goooo 👉👉👉💪🏾", debouncedURL);

        // Fire off our API call
        try {
          // setLoading(true);
          searchRecipeLink(debouncedURL, abortController)
            .then(async (result: ScrapedRecipeData) => {
              console.log("DATA FROM MIATCH", result);

              const { error, data } = result;

              if (error) {
                setLoading(true);
                setURL("");
                if (setURLFn) {
                  setURLFn("");
                }
                toast.error(error);
                return;
              }
              if (!data) {
                setLoading(true);
                setURL("");
                if (setURLFn) {
                  setURLFn("");
                }
                toast.error("There was an error");
                return;
              }

              const tempIngredients: PartialRecipe["ingredientSections"][number]["ingredients"] =
                await Promise.all(
                  data.ingredients.map(
                    async ({ text, amount, unit: unitText, name }) => {
                      const searchedIngredients = searchInventory(
                        name || text,
                        ingredients
                      ).slice(0, 5);

                      const matchingIngredients =
                        searchedIngredients
                          .map((i) => ({
                            ...i,
                            id: i.id,
                            similar: true,
                            similarity: Math.round(
                              compareTwoStrings(
                                i.name.toLowerCase(),
                                (name || text).toLowerCase()
                              ) * 100
                            ),
                          }))
                          .sort((a, b) => b.similarity - a.similarity) ?? [];

                      const [highestMatch] = matchingIngredients;
                      const highestValidMatch =
                        highestMatch && highestMatch.similarity >= 60
                          ? highestMatch
                          : null;

                      return {
                        uuid: cuid(),
                        text: text,
                        amount: Number(amount) || 1,
                        unitId: highestValidMatch
                          ? searchForUnit(
                              combineUnits(highestValidMatch),
                              unitText,
                              highestValidMatch.density ?? undefined,
                              highestValidMatch.selectionUnit ?? undefined
                            ).id
                          : undefined,
                        measurement: {
                          label: "Discrete Units",
                          value: null,
                        },
                        derivedUnit: unitText, // unit derived from the data
                        isChecked: false, // for density
                        density: 845,
                        tags: [],
                        selected: highestValidMatch || undefined,
                        options: [...matchingIngredients],
                      };
                    }
                  )
                );

              setRecipeData([
                {
                  uuid: cuid(),
                  name: data.name,
                  public: false,
                  source: data.source,
                  process: "website",
                  image: data.image,
                  servings: parseFloat(data.servings) || 1,
                  method: data.method,
                  tags: [],
                  ingredientSections: [
                    {
                      uuid: cuid(),
                      name: "Default Section",
                      section: false,
                      ingredients: tempIngredients,
                    },
                  ],
                },
              ]);

              setLoading(false);
            })
            .catch((error) => {
              if (error?.name !== "AbortError") {
                reset();
                console.error(error);

                toast.error("There was an error");
              }
            });
        } catch (error) {
          setLoading(false);
          console.error(error);

          toast.error("There was an error");
        }
      } else {
        setLoading(false);
      }

      return function cancel() {
        abortController.abort();
      };
    },
    // This is the useEffect input array
    // Our useEffect function will only execute if this value changes ...
    // ... and thanks to our hook it will only change if the original ...
    // value (searchTerm) hasn't changed for more than 500ms.
    [debouncedURL]
  );

  const reset = () => {
    setLoading(false);
    setRecipeData(null);
    setURL("");
    if (setURLFn) {
      setURLFn("");
    }
  };

  return !recipeData ? (
    <>
      <div
        className={`overflow-hidden transition-all ${
          URL ? "h-12 opacity-100" : "h-0 opacity-0"
        }`}
      >
        <button className="btn" onClick={reset}>
          Reset
        </button>
      </div>

      <h2 className="flex items-center">
        <GlobeAltIcon className="w-6 text-gray-600" />
        <span className="mx-2">Fetch Recipe from a Website</span>
      </h2>
      <p className="max-w-2xl font-medium text-cyan-600">
        If there is a recipe on the website,
        <br /> chances are ClassCart can fetch it for you!
      </p>
      <URLForm
        method="post"
        className="flex items-center justify-around"
        onSubmit={async (e) => {
          e.preventDefault();

          setLoading(true);
        }}
      >
        <URLInput
          {...{
            textInput,
            loading,
            URL,
            onChange: (e) => {
              setCanUseURL(true);
              setURL(e.target.value);
              if (setURLFn) {
                setURLFn(e.target.value);
              }
            },
          }}
        />
        {URL ? (
          <button
            type="submit"
            className="btn"
            disabled={loading}
            // style={{ marginBottom: "10px" }}
          >
            Submit
          </button>
        ) : null}
      </URLForm>
      {loading ? <BouncingLoader size={15} /> : null}
      <div className="w-full max-w-2xl rounded-md border border-solid border-cyan-200 bg-cyan-50 p-2.5 text-left font-normal text-cyan-700">
        {/* <p>
          NOTE: We&apos;ve been having trouble fetching recipes from the
          following websites:
        </p>
        <ul className="my-3 pl-5 text-cyan-900">
          <li>
            <a
              href="https://taste.com.au/"
              target="_blank"
              rel="noreferrer noopener"
            >
              taste.com.au
            </a>
          </li>
          <li>
            <a
              href="https://www.woolworths.com.au/shop/recipes"
              target="_blank"
              rel="noreferrer noopener"
            >
              woolworths.com.au/shop/recipes
            </a>
          </li>
        </ul>
        <p>
          We hope to have these issues resolved soon.{" "}
          {isTeacher(user) ? (
            <span>
              If you are getting incorrect results from a website,{" "}
              <button
                className="inline font-medium text-cyan-900 hover:text-black"
                onClick={() => {
                  (window as any).Intercom(
                    "showNewMessage",
                    "Hi, I'm getting incorrect results when fetching recipes from: "
                  );
                }}
              >
                let us know
              </button>{" "}
              and we&apos;ll try to resolve the issue.
            </span>
          ) : null}
        </p> */}
        <p>
          If you find recipes from a certain website aren&apos;t fetching
          properly,{" "}
          <button
            className="inline font-medium text-cyan-900 hover:text-black"
            onClick={() => {
              (window as any).Intercom(
                "showNewMessage",
                "Hi, I'm getting incorrect results when fetching recipes from: "
              );
            }}
          >
            let us know
          </button>{" "}
          and we&apos;ll look into it.
        </p>
      </div>
    </>
  ) : (
    <>
      <button
        className="btn"
        onClick={() => {
          setLoading(false);
          setRecipeData(null);

          setURL("");
          if (setURLFn) {
            setURLFn("");
          }
          setTimeout(() => {
            if (textInput.current != document.activeElement) {
              textInput.current?.focus();
            }
          }, 500);
        }}
      >
        Upload Again
      </button>
      {loading ? (
        <BouncingLoader size={15} />
      ) : (
        <FinalisePartialRecipe
          initialRecipes={recipeData}
          updateRecipes={setRecipeData}
          onCompleted={(recipeIDs) => {
            // toast, with a link to the recipe if only one recipe was created
            toast.success((t) => (
              <div>
                {recipeIDs.length > 1 ? (
                  <p>
                    {recipeIDs.length} recipes have been created. You can view
                    and edit them from the recipes page.
                  </p>
                ) : (
                  <p>
                    <span className="font-normal">{recipeData[0].name}</span>{" "}
                    has been created. You can view and edit it from the recipes
                    page.
                  </p>
                )}
                {recipeIDs.length === 1 ? (
                  <Link
                    prefetch={false}
                    href={`/recipes?folder=${recipeIDs[0].folderID || ""}#${
                      recipeIDs[0].id
                    }`}
                    className="btn btn-sm btn-subtle"
                    onClick={() => {
                      toast.dismiss(t.id);
                    }}
                  >
                    View Recipe
                  </Link>
                ) : null}
              </div>
            ));

            reset();

            if (onCompleted) {
              onCompleted();
            }
          }}
        />
      )}
    </>
  );
};

interface URLInputProps {
  textInput: RefObject<HTMLInputElement | null>;
  loading: boolean;
  URL: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const URLInput: React.FC<URLInputProps> = ({
  textInput,
  loading,
  URL,
  onChange,
}) => {
  const [inputPlaceholder, setInputPlaceholder] = useState(
    "Paste a Recipe Link Here"
  );

  useEffect(() => {
    let count = 0;

    const handler = () => {
      count++;

      setInputPlaceholder(
        count % 3 == 0
          ? "Paste a Recipe Link Here"
          : supportedRecipeSites
              .sort((a, b) => Math.random() - 0.5)[0]
              .URL.replace(/https?:\/\/(www)?\.?/, "") + "..."
      );
    };

    const interval = setInterval(handler, 2.5 * 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <input
      ref={textInput}
      type="url"
      name="recipe-link"
      id="recipe-link"
      className="flex-1 rounded border border-solid border-gray-300 p-2.5 text-2xl transition hover:border-gray-400"
      placeholder={inputPlaceholder}
      required
      disabled={loading}
      value={URL}
      onChange={onChange}
      autoFocus
    />
  );
};

export default FetchRecipe;
